import React, { useState, useEffect } from "react";
import api from "./Api.js";
import "./Address.css";

const Address = () => {
  const [addresses, setAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({ name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/api/admin/address/GetAll");
      if (Array.isArray(response.data)) {
        setAddresses(response.data);
      } else if (response.data?.$values) {
        setAddresses(response.data.$values);
      } else {
        setAddresses([]);
      }
    } catch (err) {
      setError("Failed to fetch addresses.");
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.post("/api/admin/address/Create", formData);
      setAddresses([...addresses, response.data]);
      setFormData({ name: "" });
      setSuccess("Address created successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Failed to create address.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.put(
        `/api/admin/address/Update/${editingAddress.id}`,
        { id: editingAddress.id, name: formData.name }
      );
      setAddresses(
        addresses.map((a) => (a.id === editingAddress.id ? response.data : a))
      );
      setEditingAddress(null);
      setFormData({ name: "" });
      setSuccess("Address updated successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Failed to update address.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      setLoading(true);
      await api.delete(`/api/admin/address/Delete/${id}`);
      setAddresses(addresses.filter((a) => a.id !== id));
      setSuccess("Address deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Failed to delete address.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({ name: address.name });
  };

  const cancelEdit = () => {
    setEditingAddress(null);
    setFormData({ name: "" });
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const filteredAddresses = addresses.filter((a) =>
    a.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="address-container">
      <div className="dashboard-header">
        <h1>Address Management Dashboard</h1>
        <p>Manage all address in the system</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card">
        <div className="card-header">
          <h3>{editingAddress ? "Edit Address" : "Add New Address"}</h3>
        </div>
        <div className="card-body">
          <form
            className="address-form"
            onSubmit={editingAddress ? handleUpdate : handleCreate}
          >
            <div className="form-group">
              <label htmlFor="name">Address Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter address name"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {editingAddress ? "Update" : "Add"}
              </button>
              {editingAddress && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Address List</h3>
          {/* 🔍 Search Input */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Search address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <span className="badge">{addresses.length}</span>
        </div>

        <div className="card-body">
          {loading && addresses.length === 0 ? (
            <p>Loading...</p>
          ) : filteredAddresses.length === 0 ? (
            <p>No addresses found.</p>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAddresses.map((address, index) => (
                    <tr key={address.id}>
                      <td>{index + 1}</td>
                      <td>{address.name}</td>
                      <td>
                        <button
                          onClick={() => handleEdit(address)}
                          className="btn-edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(address.id)}
                          className="btn-danger"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Address;
